<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'

const emit = defineEmits(['adicionar-item', 'editar-item', 'excluir-item'])

const authStore = useAuthStore()

const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

// --- Estado Local ---
const empresas = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const filtros = reactive({
  nomeEmpresa: ''
})

// --- Estado da Auditoria ---
const mostrarAuditoria = ref(false)
const empresaSelecionadaParaAuditoria = ref(null)

// --- Permissões ---
const temPermissaoCompleta = computed(() => authStore.pode('EMPRESAS_COMPLETO') || authStore.isAdminInterno)
const podeVerAcoes = computed(() => temPermissaoCompleta.value || authStore.pode('EMPRESAS_VISUALIZACAO'))

// Guarda multi-organização: sem organização ativa não há escopo de dados para exibir
const temOrganizacaoAtiva = computed(() => !!authStore.organizacaoAtiva)

const colunas = computed(() => {
  const cols = [
    { field: 'idEmpresa', label: 'ID', width: 100, sortable: true, slotName: 'id' },
    { field: 'nomeEmpresa', label: 'Nome da Empresa', sortable: true, class: 'fw-medium' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 150, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field) => {
  if (!field.startsWith('lst')) return null
  return null
}

const handleSortChange = () => {
  buscarDados(1)
}

// --- Métodos ---
const buscarDados = async (pageNumber = 1) => {
  // Sem organização ativa: não chama a API e limpa a listagem
  if (!temOrganizacaoAtiva.value) {
    empresas.value = []
    erro.value = null
    paginacao.value.TotalCount = 0
    loading.value = false
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (filtros.nomeEmpresa) params.append('nomeEmpresa', filtros.nomeEmpresa)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/Empresas?${params.toString()}`)

    empresas.value = response.data

    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar empresas:', error)
    erro.value = 'Não foi possível carregar os dados das empresas.'
    empresas.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => {
  buscarDados(1)
}

const limparFiltros = () => {
  filtros.nomeEmpresa = ''
  buscarDados(1)
}

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) {
    buscarDados(page)
  }
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const adicionarEmpresa = () => {
  emit('adicionar-item')
}

const editarEmpresa = (empresa) => {
  emit('editar-item', empresa)
}

const abrirAuditoria = (empresa) => {
  empresaSelecionadaParaAuditoria.value = empresa
  mostrarAuditoria.value = true
}

// --- Inicialização ---
onMounted(() => {
  buscarDados(1)
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage || 1)
})

// Recarrega (ou limpa) a listagem quando a organização ativa muda
watch(() => authStore.organizacaoAtiva, () => {
  buscarDados(1)
})

defineExpose({
  buscarDados
})
</script>

<template>
  <PaginaAdministrar
    titulo="Empresas Cliente"
    subtitulo="Listagem e gerenciamento de registos"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="adicionarEmpresa"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta && temOrganizacaoAtiva"
  >
    <template #acoes="{ toggleFiltros }">
      <button v-if="temOrganizacaoAtiva" class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i>
        <span>Filtros</span>
      </button>
    </template>

    <template #filtros>
      <template v-if="temOrganizacaoAtiva">
        <h3 class="filtros-titulo">Filtrar por:</h3>
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label for="filtro-nome" class="form-label">Nome da Empresa</label>
            <input type="text" id="filtro-nome" class="form-control" v-model="filtros.nomeEmpresa" placeholder="Digite para buscar..." @keydown.enter.prevent="aplicarFiltros">
          </div>

          <div class="col-auto">
            <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
            <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
          </div>
        </div>
      </template>
    </template>

    <div v-if="!temOrganizacaoAtiva" class="alert alert-warning m-3 border-0 shadow-sm">
      <i class="bi bi-diagram-3 me-2"></i> Selecione uma organização no topo.
    </div>

    <div v-else-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">A carregar...</span>
      </div>
    </div>

    <div v-else-if="erro" class="alert alert-danger m-3 border-0 shadow-sm">
      <i class="bi bi-info-circle-fill me-2"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="empresas"
      :loading="loading"
      empty-message="Nenhum registo encontrado."
      row-key="idEmpresa"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarEmpresa($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idEmpresa }}</span>
      </template>
      <template #acoes="{ row }">
        <div class="acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-primary" title="Editar" @click="editarEmpresa(row)">
            <i class="bi bi-pencil-fill"></i>
          </button>
          <button class="btn-icon text-secondary" title="Ver Histórico" @click="abrirAuditoria(row)">
            <i class="bi bi-clock-history"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria && empresaSelecionadaParaAuditoria"
    tabela="Empresa"
    contexto="master"
    :chave-primaria="empresaSelecionadaParaAuditoria.idEmpresa"
    :titulo-registro="empresaSelecionadaParaAuditoria.nomeEmpresa"
    :tabelas-filhas="['EmpresaRegraAcesso']"
    chave-estrangeira="IdEmpresa"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
.filtros-titulo {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}
</style>
